export type RosterTab = 'all' | 'pending' | 'archived' | 'blocked' | 'declined'

export const ROSTER_PAGE_SIZE = 10

export interface RosterFilterSelection {
  search: string
  ordering: '' | 'name' | '-name'
  hasRegistered: boolean[]
  hasInvite: boolean[]
  tags: string[]
  groups: string[]
  programs: string[]
  competitions: string[]
}

export interface RosterListParams extends RosterFilterSelection {
  tab: RosterTab
  segment?: string
  page: number
  pageSize: number
}

export interface RosterPage<T> {
  count: number
  page: number
  pageSize: number
  results: T[]
}

export interface RosterSummary {
  counts: Record<RosterTab, number>
  filters: {
    tags: string[]
    groups: string[]
    programs: string[]
    competitions: string[]
  }
}

export const emptyRosterFilters = (): RosterFilterSelection => ({
  search: '',
  ordering: '',
  hasRegistered: [],
  hasInvite: [],
  tags: [],
  groups: [],
  programs: [],
  competitions: [],
})

export const rosterFiltersFromQuery = (
  query: RosterFilterSelection
): RosterFilterSelection => ({
  search: query.search,
  ordering: query.ordering,
  hasRegistered: query.hasRegistered,
  hasInvite: query.hasInvite,
  tags: query.tags,
  groups: query.groups,
  programs: query.programs,
  competitions: query.competitions,
})

export const defaultRosterOrdering = (tab: RosterTab) => {
  if (tab === 'archived' || tab === 'blocked') {
    return '-archive_date'
  }
  if (tab === 'declined') {
    return '-declined_at'
  }
  return 'name'
}

const appendAll = (query: URLSearchParams, key: string, values: string[]) => {
  values.forEach((value) => {
    if (value) {
      query.append(key, value)
    }
  })
}

export const rosterListPath = (params: RosterListParams) => {
  const query = new URLSearchParams()
  query.set('page', String(params.page))
  query.set('page_size', String(params.pageSize))
  if (params.search.trim()) {
    query.set('search', params.search.trim())
  }
  query.set('ordering', params.ordering || defaultRosterOrdering(params.tab))

  if (params.tab === 'all' && params.segment) {
    query.set('segments', params.segment)
  }
  if (params.tab === 'pending') {
    query.set('approved', 'false')
  }
  if (params.tab === 'archived') {
    query.set('archived', 'true')
    query.set('block', 'false')
  }
  if (params.tab === 'blocked') {
    query.set('archived', 'true')
    query.set('block', 'true')
  }
  if (params.tab === 'all') {
    query.set('archived', 'false')
    params.hasRegistered.forEach((value) =>
      query.append('has_registered', String(value))
    )
    params.hasInvite.forEach((value) => query.append('has_invite', String(value)))
    appendAll(query, 'tag', params.tags)
    appendAll(query, 'group', params.groups)
    appendAll(query, 'program', params.programs)
    appendAll(query, 'competition', params.competitions)
  }

  const path =
    params.tab === 'all'
      ? '/hr/profile/my_commuters/'
      : params.tab === 'declined'
        ? '/hr/profile/my_declined_employees/'
        : '/hr/profile/my_employees/'
  return `${path}?${query.toString()}`
}

export const rosterSummaryPath = (segment?: string) => {
  if (!segment) {
    return '/hr/profile/my_commuters/roster_summary/'
  }
  return `/hr/profile/my_commuters/roster_summary/?segments=${encodeURIComponent(segment)}`
}
